import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import LogoTitle from '@/components/logoTitle'

// Swiper 样式
import 'swiper/swiper-bundle.css'

interface BootItem {
  image: string
  text: string
}

const FIRST_VISIT_KEY = 'hasVisitedBoot'

export default function Boot() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const swiperRef = useRef<SwiperType | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTextVisible, setIsTextVisible] = useState(true)

  // 检查登录状态，已登录用户不能访问引导页
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('token')
    if (isLoggedIn) {
      navigate('/home', { replace: true })
    }
  }, [navigate])

  const items: BootItem[] = [
    {
      image: '/images/boot/boot-swiper1.webp', // 图片资源留空
      text: t('160个国家地区40种货币一个账户')
    },
    {
      image: '/images/boot/boot-swiper2.webp',
      text: t('向境外汇款和从境外收款')
    },
    {
      image: '/images/boot/boot-swiper3.webp',
      text: t('使用通用卡片在世界各地自由支付')
    },
    {
      image: '/images/boot/boot-swiper4.webp',
      text: t('让窃贼失望')
    },
    {
      image: '/images/boot/boot-swiper5.webp',
      text: t('全球通用的国际账户')
    }
  ]

  // 标记用户已访问过引导页
  useEffect(() => {
    localStorage.setItem(FIRST_VISIT_KEY, 'true')
  }, [])

  // 处理跳过按钮
  const handleSkip = useCallback(() => {
    navigate('/login',{ replace: true })
  }, [navigate])

  // 处理滑动到最后一页后的再次滑动
  const handleSlideChange = useCallback((swiper: SwiperType) => {
    // 先淡出文字
    setIsTextVisible(false)
    
    // 延迟更新索引和淡入
    setTimeout(() => {
      setCurrentIndex(swiper.activeIndex)
      setIsTextVisible(true)
    }, 200)
  }, [])

  const handleSlideChangeTransitionEnd = useCallback((swiper: SwiperType) => {
    // 如果在最后一页且尝试继续滑动，跳转到登录页
    if (swiper.isEnd && swiper.activeIndex === items.length - 1) {
      // 这里可以添加一个延迟或者监听用户的滑动手势
    }
  }, [items.length])

  // 处理向下箭头点击
  const handleNextSlide = useCallback(() => {
    if (currentIndex === items.length - 1) {
      // 最后一页，跳转登录
      navigate('/login', { replace: true })
    } else if (swiperRef.current) {
      swiperRef.current.slideNext()
    }
  }, [currentIndex, items.length, navigate])

  return (
    <div className="bg-white h-[100dvh] flex flex-col overflow-hidden animation-fadeIn">
      {/* 顶部 Logo 和跳过按钮 */}
      <div className="flex items-center justify-center px-6 pt-4 pb-3 flex-shrink-0 relative">
        <LogoTitle className="w-18" />
        <button
          onClick={handleSkip}
          className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 border-solid rounded-full absolute right-6"
        >
          {t('跳过')}
        </button>
      </div>

      {/* Swiper 容器 - 只包含图片 */}
      <div className="flex-shrink-0 w-full overflow-hidden" style={{ height: 'min(48vh, 380px)' }}>
        <div className="h-full pt-8">
          <Swiper
            onSwiper={(swiper) => { swiperRef.current = swiper }}
            onSlideChange={handleSlideChange}
            onSlideChangeTransitionEnd={handleSlideChangeTransitionEnd}
            className="h-full w-full"
            spaceBetween={0}
            slidesPerView={1}
          >
            {items.map((item, index) => (
              <SwiperSlide key={index}>
                <div className="h-full flex items-center justify-center px-6">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={`slide-${index}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* 分页指示器 */}
      <div className="flex justify-center space-x-2 py-4 flex-shrink-0">
        {items.map((_, index) => (
          <div
            key={index}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${index === currentIndex
              ? 'bg-black'
              : 'bg-[#D0D0D0]'
              }`}
          />
        ))}
      </div>

      {/* 文字描述 - 固定显示，不跟随切换 */}
      <div className="flex-1 flex items-center justify-center px-6 py-2 min-h-0">
        <div className="text-center max-w-sm">
          <h2 
            className="text-8 font-medium leading-relaxed transition-opacity duration-300"
            style={{ opacity: isTextVisible ? 1 : 0 }}
          >
            {items[currentIndex].text}
          </h2>
        </div>
      </div>

      {/* 底部箭头按钮 */}
      <div className="flex-shrink-0 pb-6 flex justify-center">
        <button
          onClick={handleNextSlide}
          className="w-15 h-15 rounded-full border-2 border-[#D0D0D0] border-solid flex items-center justify-center"
        >
          <img src="/icon/arrow-right.svg" alt="Next" />
        </button>
      </div>
    </div>
  )
}